import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDate } from "@/lib/adminFmt";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save } from "lucide-react";

/**
 * Landing content editor + Emergent Object Storage video uploader.
 *
 * Reads the site_copy manifest at `/site/copy` (returns `{ values, manifest }`),
 * lets admins edit each known key, and saves via `PATCH /admin/site/copy`.
 * For the `landing.hero_video_url` key it also renders a drag-drop MP4 uploader
 * that posts to `/admin/uploads/video` (multipart) — the upload URL is auto-
 * inserted into the field. A recent-uploads list lets admins swap between
 * previously uploaded videos without re-uploading.
 *
 * Extracted from Admin.jsx.
 */
export default function LandingContentCard() {
  const [manifest, setManifest] = useState([]);
  const [values, setValues] = useState({});
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, u] = await Promise.all([
        api.get("/site/copy"),
        api.get("/admin/uploads/videos").catch(() => ({ data: { items: [] } })),
      ]);
      setManifest(c.data.manifest || []);
      setValues(c.data.values || {});
      setDraft(c.data.values || {});
      setUploads(u.data.items || []);
    } catch { toast.error("Failed to load site copy"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const dirty = manifest.some((m) => (draft[m.key] || "") !== (values[m.key] || ""));

  const save = async () => {
    setBusy(true);
    try {
      const updates = {};
      for (const m of manifest) {
        if ((draft[m.key] || "") !== (values[m.key] || "")) updates[m.key] = draft[m.key] || m.default;
      }
      await api.patch("/admin/site/copy", { updates });
      toast.success(`Saved — ${Object.keys(updates).length} field(s)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = (key) => {
    const m = manifest.find((x) => x.key === key);
    if (!m) return;
    setDraft({ ...draft, [key]: m.default });
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/uploads/video", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Rewrite to absolute so <video> loads without depending on origin routing quirks.
      const abs = (process.env.REACT_APP_BACKEND_URL || "") + r.data.served_url;
      setDraft((d) => ({ ...d, "landing.hero_video_url": abs }));
      toast.success(`Uploaded ${(file.size / 1024 / 1024).toFixed(1)}MB — click Save to publish`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Card className="mt-6" data-testid="landing-content-card">
      <CardContent className="p-6 space-y-4">
        <div>
          <h2 className="serif text-2xl">Landing content</h2>
          <p className="text-sm text-muted-foreground mt-1">Edit the marketing landing page content. Every previously-uploaded file is retained — swap by URL, or drag-drop a new MP4 below.</p>
        </div>

        {manifest.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">Loading…</div>}

        {manifest.map((m) => (
          <div key={m.key} className="rounded-md border border-border p-4 space-y-2" data-testid={`landing-field-${m.key}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <Label className="mono text-xs">{m.label}</Label>
                {m.help && <div className="text-xs text-muted-foreground mt-0.5">{m.help}</div>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => reset(m.key)} className="mono text-[10px]">
                Reset to default
              </Button>
            </div>
            <Input
              value={draft[m.key] || ""}
              onChange={(e) => setDraft({ ...draft, [m.key]: e.target.value })}
              placeholder={m.default}
              data-testid={`landing-input-${m.key}`}
            />
            {m.key === "landing.hero_video_url" && (
              <div className="rounded-md border-2 border-dashed border-border p-3 bg-muted/20">
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                    onChange={(e) => uploadVideo(e.target.files?.[0])}
                    className="hidden"
                    data-testid="video-upload-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    data-testid="video-upload-btn"
                    className="rounded-full"
                  >
                    {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Uploading…</> : "Upload MP4 (max 100MB)"}
                  </Button>
                  <span className="mono text-[10px] text-muted-foreground">Stored on Emergent Object Storage · unique UUID per file · previous versions never overwritten</span>
                </div>
                {uploads.length > 0 && (
                  <div className="mt-3">
                    <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recently uploaded ({uploads.length})</div>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {uploads.slice(0, 8).map((u) => {
                        const abs = (process.env.REACT_APP_BACKEND_URL || "") + u.served_url;
                        const isCurrent = (draft["landing.hero_video_url"] || "").endsWith(u.served_url);
                        return (
                          <div key={u.id} className={`flex items-center justify-between gap-2 text-xs px-2 py-1 rounded ${isCurrent ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/40"}`}>
                            <div className="min-w-0 flex-1">
                              <div className="truncate">{u.original_filename} <span className="text-muted-foreground">· {(u.size_bytes / 1024 / 1024).toFixed(1)}MB</span></div>
                              <div className="mono text-[9px] text-muted-foreground">{fmtDate(u.uploaded_at)} · by {u.uploaded_by}</div>
                            </div>
                            {isCurrent ? (
                              <Badge className="mono text-[9px] uppercase">Current</Badge>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => setDraft({ ...draft, "landing.hero_video_url": abs })} data-testid={`video-pick-${u.id}`}>Use</Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {draft[m.key] && draft[m.key].match(/\.(mp4|webm|mov)(\?|$)/i) && (
              <video
                key={draft[m.key]}
                src={draft[m.key]}
                muted playsInline autoPlay loop
                className="rounded-md border border-border w-full max-w-xs mt-2 bg-black"
                onError={() => toast.error("Video failed to load — check the URL")}
              />
            )}
          </div>
        ))}

        {manifest.length > 0 && (
          <div className="flex items-center gap-3 pt-2">
            <Button onClick={save} disabled={!dirty || busy} className="rounded-full" data-testid="landing-save">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save changes</>}
            </Button>
            {!dirty && <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground">No unsaved changes</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
